import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  CalendarIcon,
  CloseIcon,
  ClockIcon,
  CloudUploadIcon,
  EditIcon,
  EyeIcon,
  FilterIcon,
  PathIcon,
  PhoneIcon,
  PlusIcon,
  PulseMiniIcon,
  SearchIcon,
  UserGroupIcon,
} from "../../lib/icons";
import { formatDateCell, formatTimeSince, initialsForName } from "../../lib/utils";
import DateRangeModal from "../leads/DateRangeModal";

const BREAK_OPTIONS = [
  { value: "short_break", label: "Short Break" },
  { value: "lunch", label: "Lunch" },
  { value: "training", label: "Training" },
];

function breakLabel(value) {
  const opt = BREAK_OPTIONS.find((o) => o.value === value);
  return opt ? opt.label : "No Break";
}

const AGENTS_AUTO_REFRESH_MS = 60_000;
const AGENTS_STATUS_REFRESH_MS = 5_000;

function SummaryCard({ label, value, icon, tone, breakdown }) {
  return (
    <article className="agents-summary-card">
      <div>
        <p>{label}</p>
        <strong>{value}</strong>
        {breakdown && (
          <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
            {breakdown.map((item) => (
              <span key={item.label} style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: item.bg || 'rgba(0,0,0,0.06)', color: item.color || 'inherit', whiteSpace: 'nowrap' }}>
                {item.label} {item.value.toLocaleString('en-IN')}
              </span>
            ))}
          </div>
        )}
      </div>
      <span className={`agents-summary-icon ${tone}`}>{icon}</span>
    </article>
  );
}

const AGENT_COLUMN_DEFINITIONS = [
  { col: "Call Activity",    def: "Live availability status of the agent right now (Online, On Call, Break, Offline)." },
  { col: "Active Time / Day",def: "Average total time the agent spent on calls per calendar day in the selected period." },
  { col: "Assigned Leads",   def: "Total leads currently assigned to this agent." },
  { col: "Calls Attempted",  def: "Total number of call attempts made by this agent, including missed, dropped, and connected calls." },
  { col: "Unique Attempts",  def: "Number of distinct leads this agent dialled at least once. Multiple calls to the same lead count as one." },
  { col: "Connected Calls",  def: "Calls that were answered and lasted longer than the minimum duration threshold (default 10 seconds)." },
  { col: "Unique Connected", def: "Number of distinct leads the agent actually spoke with (had at least one connected call)." },
  { col: "Talk Time",        def: "Total duration of all connected calls (calls that were answered). Excludes unanswered attempts." },
  { col: "Dialed Time",      def: "Total time spent dialling, including connected and unanswered calls." },
  { col: "Break",            def: "Total time the agent was on a break status today." },
];

function AgentColumnInfoModal({ onClose }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel col-info-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-panel-head">
          <div>
            <h3>Agent Table — Column Guide</h3>
            <p style={{ fontSize: "0.8rem", color: "#99a5ba", margin: 0 }}>What each metric means</p>
          </div>
          <button type="button" className="icon-button" onClick={onClose}>✕</button>
        </div>
        <div className="col-info-modal-body">
          {AGENT_COLUMN_DEFINITIONS.map(({ col, def }) => (
            <div key={col} className="col-info-row">
              <span className="col-info-name">{col}</span>
              <span className="col-info-def">{def}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function InfoIconButton({ onClick }) {
  return (
    <button type="button" className="col-info-trigger" onClick={onClick} title="Column guide" aria-label="What do these columns mean?">
      <svg viewBox="0 0 16 16" fill="currentColor" width="15" height="15">
        <path d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8Z"/>
        <path d="M7.25 6.75A.75.75 0 0 1 8 6h.01a.75.75 0 0 1 0 1.5H8a.75.75 0 0 1-.75-.75ZM6.5 10.25a.75.75 0 0 1 .75-.75h.75V8.5H7.25a.75.75 0 0 1 0-1.5H8.5a.75.75 0 0 1 .75.75v2h.25a.75.75 0 0 1 0 1.5h-2.25a.75.75 0 0 1-.75-.75Z"/>
      </svg>
    </button>
  );
}

function AgentTableHeading({ icon, iconTone, children }) {
  return (
    <span className="agents-th-label">
      <span className={`agents-th-icon${iconTone ? ` agents-th-icon--${iconTone}` : ""}`} aria-hidden="true">{icon}</span>
      <span>{children}</span>
    </span>
  );
}

function bucketForLeadLoad(value) {
  if (value >= 100) return "High";
  if (value >= 50) return "Medium";
  return "Low";
}

function bucketForPerformance(value) {
  if (value >= 75) return "High";
  if (value >= 50) return "Medium";
  return "Low";
}

function uniqueSorted(values) {
  return Array.from(new Set(values.filter(Boolean))).sort((left, right) => left.localeCompare(right));
}

function formatDuration(seconds) {
  const totalSeconds = Math.max(0, Number(seconds) || 0);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  if (hours) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
}

function formatReportDateLabel(value) {
  if (!value) return "selected day";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "selected day";
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function formatShortDate(iso) {
  if (!iso) return "";
  const [year, month, day] = iso.split("-");
  if (!year || !month || !day) return "";
  return `${day}/${month}/${year.slice(2)}`;
}

function todayISO() {
  const today = new Date();
  const localTime = today.getTime() - today.getTimezoneOffset() * 60_000;
  return new Date(localTime).toISOString().slice(0, 10);
}

function visibleAvailabilityStatus(agent) {
  if (agent?.daily_active_status !== "active") {
    return "offline";
  }
  return agent?.availability_status || "offline";
}

function availabilityLabel(agent) {
  const visibleStatus = visibleAvailabilityStatus(agent);
  if (visibleStatus === "break" && agent.break_reason) {
    return breakLabel(agent.break_reason);
  }
  return visibleStatus
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function availabilityTone(agent) {
  const visibleStatus = visibleAvailabilityStatus(agent);
  if (visibleStatus === "online") return "green";
  if (visibleStatus === "on_call") return "blue";
  if (visibleStatus === "break") return "gold";
  return "gray";
}

function splitNameParts(value) {
  const parts = String(value || "").trim().split(/\s+/).filter(Boolean);
  return {
    first_name: parts[0] || "",
    last_name: parts.slice(1).join(" "),
  };
}

function regionNamesForAgent(agent) {
  const regions = (agent.geographies || []).map((geo) => {
    if (geo.node_type === "region") {
      return geo.name;
    }
    if (geo.display_name) {
      return String(geo.display_name).split(" / ")[0];
    }
    return "";
  });

  return uniqueSorted([...regions, agent.region || ""]);
}

function AgentDetailModal({ agent, onAddLead, onClose, onManageAgent }) {
  if (!agent) {
    return null;
  }

  const regions = regionNamesForAgent(agent);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <p className="modal-kicker">Agent Details</p>
            <h2>{agent.name}</h2>
          </div>
          <button type="button" className="close-button" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="form-grid">
          <div className="field-stack">
            <span>Email</span>
            <div className="subtext-cell">{agent.email || "—"}</div>
          </div>
          <div className="field-stack">
            <span>Phone</span>
            <div className="subtext-cell">{agent.phone_number || "—"}</div>
          </div>
          <div className="field-stack">
            <span>Manager</span>
            <div className="subtext-cell">{agent.manager_name || "—"}</div>
          </div>
          <div className="field-stack">
            <span>Regions</span>
            <div className="subtext-cell">{regions.join(", ") || "—"}</div>
          </div>

          <div className="field-stack full-span">
            <span>Geographies</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {(agent.geographies || []).length ? (
                agent.geographies.map((geo) => (
                  <span key={geo.id} className="quick-filter-selection-pill">
                    {geo.display_name || geo.name}
                  </span>
                ))
              ) : (
                <span className="subtext-cell">No geographies assigned.</span>
              )}
            </div>
          </div>

          <div className="field-stack full-span">
            <span>Recent Leads</span>
            {(agent.recent_leads || []).length ? (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Lead ID</th>
                      <th>Name</th>
                      <th>City</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agent.recent_leads.map((lead) => (
                      <tr key={lead.id}>
                        <td className="mono-text">{lead.client_id}</td>
                        <td>{lead.full_name}</td>
                        <td>{lead.city_name || "—"}</td>
                        <td>{lead.current_disposition_name || lead.status || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="subtext-cell">No recent leads.</div>
            )}
          </div>

          <div className="modal-actions full-span">
            <button type="button" className="secondary-ghost" onClick={() => onManageAgent(agent)}>
              Manage Agent
            </button>
            <button type="button" className="primary-button modal-submit" onClick={() => onAddLead(agent)}>
              Add Lead For Agent
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditAgentModal({ agent, draft, managers, onChange, onClose, onSubmit, saving }) {
  if (!agent) {
    return null;
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <p className="modal-kicker">Edit Agent</p>
            <h2>{agent.name}</h2>
          </div>
          <button type="button" className="close-button" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <form className="form-grid" onSubmit={onSubmit}>
          <label className="field-stack">
            <span>Full Name</span>
            <input value={draft.name || ""} onChange={(event) => onChange("name", event.target.value)} />
          </label>
          <label className="field-stack">
            <span>Email</span>
            <input type="email" value={draft.email || ""} onChange={(event) => onChange("email", event.target.value)} />
          </label>
          <label className="field-stack">
            <span>Phone</span>
            <input value={draft.phone_number || ""} onChange={(event) => onChange("phone_number", event.target.value)} />
          </label>
          <label className="field-stack">
            <span>Reporting To</span>
            <select value={draft.reports_to || ""} onChange={(event) => onChange("reports_to", event.target.value || null)}>
              <option value="">— No manager —</option>
              {managers.map((mgr) => (
                <option key={mgr.id} value={mgr.id}>{mgr.full_name}</option>
              ))}
            </select>
          </label>
          <label className="field-stack">
            <span>Ozonetel Agent ID</span>
            <input
              value={draft.ozonetel_agent_id || ""}
              onChange={(event) => onChange("ozonetel_agent_id", event.target.value)}
              placeholder="crm_agent_a"
            />
          </label>

          <div className="modal-actions full-span">
            <button type="submit" className="primary-button modal-submit" disabled={saving}>
              {saving ? "Saving..." : "Save Agent"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PhoneRevealModal({ agent, error, loading, onClose, reveals }) {
  if (!agent) {
    return null;
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <p className="modal-kicker">Phone Reveal Log</p>
            <h2>{agent.name}</h2>
          </div>
          <button type="button" className="close-button" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {error ? <p className="message-banner error">{error}</p> : null}

        <div className="phone-reveal-list">
          {loading ? (
            <div className="phone-reveal-empty">Loading reveal history...</div>
          ) : reveals.length ? (
            reveals.map((item) => (
              <article className="phone-reveal-row" key={item.id}>
                <div>
                  <strong>{item.phone_number || "Phone unavailable"}</strong>
                  <p>
                    {item.lead_name || "Unknown lead"}
                    {item.client_id ? ` · ${item.client_id}` : ""}
                  </p>
                </div>
                <time>{formatDateCell(item.occurred_at)}</time>
              </article>
            ))
          ) : (
            <div className="phone-reveal-empty">No phone reveals recorded for this agent.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function MyAgentsView({
  backendGeographies = [],
  currentUser,
  onAddAgent,
  onAssignLead,
  onBulkReassign,
  onUploadLeads,
}) {
  const isAdmin = currentUser?.role === "admin";
  const isManager = currentUser?.role === "manager";

  const [agents, setAgents] = useState([]);
  const [agentSummary, setAgentSummary] = useState(null);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [columnInfoOpen, setColumnInfoOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [editingAgent, setEditingAgent] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [phoneRevealAgent, setPhoneRevealAgent] = useState(null);
  const [phoneRevealLogs, setPhoneRevealLogs] = useState([]);
  const [phoneRevealLoading, setPhoneRevealLoading] = useState(false);
  const [phoneRevealError, setPhoneRevealError] = useState("");
  const [savingAgent, setSavingAgent] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [reportDateFrom, setReportDateFrom] = useState(() => todayISO());
  const [reportDateTo, setReportDateTo] = useState(() => todayISO());
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [breakSaving, setBreakSaving] = useState({});

  const fetchAgentsInFlightRef = useRef(false);
  const fetchStatusInFlightRef = useRef(false);
  const [filters, setFilters] = useState({
    activeStatus: "All",
    geography: "All Regions",
    leadLoad: "All Loads",
    manager: "All Managers",
    performance: "All Levels",
    status: "All Status",
  });
  const reportDate = reportDateTo;
  const isViewingToday = reportDateTo === todayISO() && reportDateFrom === todayISO();
  const reportDateLabel = formatReportDateLabel(reportDateTo);
  const performanceDateLabel = (() => {
    if (!reportDateFrom && !reportDateTo) return "All Dates";
    const from = formatShortDate(reportDateFrom);
    const to = formatShortDate(reportDateTo);
    if (from === to || !reportDateTo || reportDateFrom === reportDateTo) return `Date: ${from}`;
    return `${from} – ${to}`;
  })();

  useEffect(() => {
    let active = true;

    function buildAgentsQuery(options = {}) {
      const query = new URLSearchParams();
      if (filters.geography !== "All Regions") {
        const targetRegion = (backendGeographies || []).find(
          (geo) => geo.node_type === "region" && geo.name === filters.geography,
        );
        if (targetRegion?.id) {
          query.set("geography_id", String(targetRegion.id));
        }
      }
      if (reportDateFrom) {
        query.set("date_from", reportDateFrom);
        query.set("date_to", reportDateTo || reportDateFrom);
      }
      if (options.silent) {
        query.set("_ts", String(Date.now()));
      }
      return query;
    }

    async function fetchAgents(options = {}) {
      if (
        (options.silent && fetchAgentsInFlightRef.current) ||
        (options.silent && document.visibilityState === "hidden")
      ) {
        return;
      }

      fetchAgentsInFlightRef.current = true;
      try {
        if (!options.silent) {
          setLoading(true);
        }
        setError("");
        const query = buildAgentsQuery(options);
        const response = await fetch(`/api/crm/manager/agents/${query.toString() ? `?${query}` : ""}`, {
          credentials: "include",
          cache: "no-store",
          headers: {
            "Content-Type": "application/json",
            "Cache-Control": "no-cache",
            "X-Requested-With": "XMLHttpRequest",
          },
        });
        if (!response.ok) {
          throw new Error(`Failed to load agents (status ${response.status}).`);
        }
        const data = await response.json();
        if (active) {
          setAgents(data.agents || []);
          setAgentSummary(data.summary || null);
          setHasLoadedOnce(true);
        }
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        fetchAgentsInFlightRef.current = false;
        if (active && !options.silent) setLoading(false);
      }
    }

    async function fetchAgentStatuses() {
      if (fetchStatusInFlightRef.current || document.visibilityState === "hidden") {
        return;
      }

      fetchStatusInFlightRef.current = true;
      try {
        const query = buildAgentsQuery({ silent: true });
        const response = await fetch(`/api/crm/manager/agents/status/${query.toString() ? `?${query}` : ""}`, {
          credentials: "include",
          cache: "no-store",
          headers: {
            "Content-Type": "application/json",
            "Cache-Control": "no-cache",
            "X-Requested-With": "XMLHttpRequest",
          },
        });
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        if (!active) {
          return;
        }
        const statusesById = new Map((data.statuses || []).map((item) => [item.id, item]));
        setAgents((current) =>
          current.map((agent) => {
            const live = statusesById.get(agent.id);
            return live ? { ...agent, ...live } : agent;
          })
        );
      } finally {
        fetchStatusInFlightRef.current = false;
      }
    }

    fetchAgents();
    fetchAgentStatuses();

    const id = setInterval(() => fetchAgents({ silent: true }), AGENTS_AUTO_REFRESH_MS);
    const statusId = setInterval(fetchAgentStatuses, AGENTS_STATUS_REFRESH_MS);
    function handleVisibility() {
      if (document.visibilityState === "visible") {
        fetchAgents({ silent: true });
        fetchAgentStatuses();
      }
    }
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      active = false;
      fetchAgentsInFlightRef.current = false;
      fetchStatusInFlightRef.current = false;
      clearInterval(id);
      clearInterval(statusId);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [backendGeographies, filters.geography, reportDateFrom, reportDateTo, refreshKey]);

  useEffect(() => {
    fetch('/api/accounts/users/?role=manager', {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => { if (data) setManagers(data.results || data); })
      .catch(() => {});
  }, []);

  async function requestJson(url, options = {}) {
    const response = await fetch(url, {
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        ...(options.headers || {}),
      },
      ...options,
    });

    if (!response.ok) {
      let message = `Request failed (status ${response.status}).`;
      try {
        const payload = await response.json();
        message = payload?.detail || payload?.message || message;
      } catch (_error) {
        // ignore parse failure
      }
      throw new Error(message);
    }

    if (response.status === 204) {
      return null;
    }

    return response.json();
  }

  const managerOptions = useMemo(
    () => ["All Managers", ...Array.from(new Set(agents.map((agent) => agent.manager_name).filter(Boolean))).sort()],
    [agents],
  );
  const geographyOptions = useMemo(
    () => [
      "All Regions",
      ...uniqueSorted(
        (backendGeographies || [])
          .filter((geo) => geo.node_type === "region")
          .map((geo) => geo.name),
      ),
    ],
    [backendGeographies],
  );

  const filteredAgents = useMemo(() => {
    const query = search.trim().toLowerCase();
    return agents.filter((agent) => {
      const performanceValue = Math.max(Number(agent.contact_rate) || 0, Number(agent.conversion_rate) || 0);
      const agentRegions = regionNamesForAgent(agent);
      if (query) {
        const haystack = [
          agent.name,
          agent.email,
          agent.manager_name,
          agent.region,
          ...agentRegions,
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(query)) {
          return false;
        }
      }
      if (filters.activeStatus === "Recently Active" && agent.daily_active_status !== "active") {
        return false;
      }
      if (filters.activeStatus === "Idle" && agent.daily_active_status === "active") {
        return false;
      }
      if (filters.manager !== "All Managers" && agent.manager_name !== filters.manager) {
        return false;
      }
      if (filters.geography !== "All Regions" && !agentRegions.includes(filters.geography)) {
        return false;
      }
      if (filters.status !== "All Status" && visibleAvailabilityStatus(agent) !== filters.status.toLowerCase()) {
        return false;
      }
      if (filters.leadLoad !== "All Loads" && bucketForLeadLoad(Number(agent.active_leads) || 0) !== filters.leadLoad) {
        return false;
      }
      if (filters.performance !== "All Levels" && bucketForPerformance(performanceValue) !== filters.performance) {
        return false;
      }
      return true;
    });
  }, [agents, filters, search]);

  const summary = useMemo(() => {
    const totals = filteredAgents.reduce(
      (accumulator, agent) => {
        accumulator.activeLeads += Number(agent.active_leads) || 0;
        accumulator.assignedToday += Number(agent.leads_today) || 0;
        accumulator.conversionRateTotal += Number(agent.conversion_rate) || 0;
        return accumulator;
      },
      { activeLeads: 0, assignedToday: 0, conversionRateTotal: 0 },
    );
    const hasAgentFilters =
      Boolean(search.trim()) ||
      filters.activeStatus !== "All" ||
      filters.geography !== "All Regions" ||
      filters.manager !== "All Managers" ||
      filters.status !== "All Status" ||
      filters.leadLoad !== "All Loads" ||
      filters.performance !== "All Levels";
    const useGlobalSummary = !hasAgentFilters && agentSummary?.total_leads != null;
    return {
      totalAgents: filteredAgents.length,
      totalLeads: useGlobalSummary
        ? Number(agentSummary.total_leads) || 0
        : totals.activeLeads,
      b2cLeads: useGlobalSummary ? Number(agentSummary.b2c_leads) || 0 : null,
      franchiseLeads: useGlobalSummary ? Number(agentSummary.franchise_leads) || 0 : null,
      activeLeads: totals.activeLeads,
      assignedToday: totals.assignedToday,
      conversionRate: filteredAgents.length
        ? `${(totals.conversionRateTotal / filteredAgents.length).toFixed(1)}%`
        : "0.0%",
    };
  }, [agentSummary, filteredAgents, filters.activeStatus, filters.geography, filters.leadLoad, filters.manager, filters.performance, filters.status, search]);

  const topPerformer = useMemo(() => {
    if (!filteredAgents.length) return null;
    return filteredAgents.reduce((best, agent) => {
      const rate = Number(agent.conversion_rate) || 0;
      const bestRate = best ? Number(best.conversion_rate) || 0 : -1;
      return rate > bestRate ? agent : best;
    }, null);
  }, [filteredAgents]);

  const attention = useMemo(() => {
    const zeroConversion = filteredAgents.filter(
      (agent) => (Number(agent.active_leads) || 0) > 0 && (Number(agent.conversion_rate) || 0) === 0,
    ).length;
    const overdue = filteredAgents.reduce(
      (sum, agent) => sum + (Number(agent.overdue_count) || 0),
      0,
    );
    const inactive = filteredAgents.filter((agent) => agent.daily_active_status !== "active").length;
    return { zeroConversion, overdue, inactive };
  }, [filteredAgents]);

  function updateFilter(field, value) {
    setFilters((current) => ({ ...current, [field]: value }));
  }

  function resetFilters() {
    setSearch("");
    setReportDateFrom("");
    setReportDateTo("");
    setFilters({
      activeStatus: "All",
      geography: "All Regions",
      leadLoad: "All Loads",
      manager: "All Managers",
      performance: "All Levels",
      status: "All Status",
    });
  }

  async function handleDeactivateAgent(agent) {
    if (!agent) return;
    if (!window.confirm(`Deactivate ${agent.name}? They will lose access to the CRM.`)) return;
    try {
      setSavingAgent(true);
      setError("");
      setNotice("");
      await requestJson(`/api/accounts/users/${agent.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: false }),
      });
      setNotice(`${agent.name} has been deactivated.`);
      setSelectedAgent(null);
      setEditingAgent(null);
      setEditDraft({});
      setRefreshKey((current) => current + 1);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSavingAgent(false);
    }
  }

  async function handleActivateAgent(agent) {
    if (!agent) return;
    try {
      setSavingAgent(true);
      setError("");
      setNotice("");
      await requestJson(`/api/accounts/users/${agent.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: true }),
      });
      setNotice(`${agent.name} has been activated.`);
      setRefreshKey((current) => current + 1);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSavingAgent(false);
    }
  }

  function openEditAgent(agent) {
    setSelectedAgent(null);
    setEditingAgent(agent);
    setEditDraft({
      name: agent.name || "",
      email: agent.email || "",
      phone_number: agent.phone_number || "",
      reports_to: agent.reports_to_id ? String(agent.reports_to_id) : "",
      ozonetel_agent_id: agent.ozonetel_agent_id || "",
    });
  }

  async function openPhoneRevealLog(agent) {
    setPhoneRevealAgent(agent);
    setPhoneRevealLogs([]);
    setPhoneRevealError("");
    setPhoneRevealLoading(true);
    try {
      const params = new URLSearchParams({ agent_id: String(agent.id) });
      const payload = await requestJson(`/api/crm/manager/phone-reveals/?${params.toString()}`);
      setPhoneRevealLogs(payload?.reveals || []);
    } catch (requestError) {
      setPhoneRevealError(requestError.message || "Failed to load phone reveal history.");
    } finally {
      setPhoneRevealLoading(false);
    }
  }

  async function handleEditAgentSubmit(event) {
    event.preventDefault();
    if (!editingAgent) {
      return;
    }

    try {
      setSavingAgent(true);
      setError("");
      setNotice("");
      const nameParts = splitNameParts(editDraft.name);
      await requestJson(`/api/accounts/users/${editingAgent.id}/`, {
        method: "PATCH",
        body: JSON.stringify({
          first_name: nameParts.first_name,
          last_name: nameParts.last_name,
          email: editDraft.email,
          phone_number: editDraft.phone_number,
          ozonetel_agent_id: editDraft.ozonetel_agent_id,
          reports_to: editDraft.reports_to || null,
          role: "agent",
        }),
      });
      setNotice("Agent details updated.");
      setEditingAgent(null);
      setEditDraft({});
      setRefreshKey((current) => current + 1);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSavingAgent(false);
    }
  }

  async function handleBreakChange(agent, breakReasonValue) {
    setBreakSaving((s) => ({ ...s, [agent.id]: true }));
    try {
      const payload = await requestJson("/api/crm/manager/agents/", {
        method: "POST",
        body: JSON.stringify({ agent_id: agent.id, break_reason: breakReasonValue }),
      });
      setAgents((prev) =>
        prev.map((a) => a.id === agent.id ? {
          ...a,
          break_reason: payload?.break_reason || "",
          availability_status: payload?.availability_status || a.availability_status,
        } : a)
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setBreakSaving((s) => ({ ...s, [agent.id]: false }));
    }
  }

  if (loading && !hasLoadedOnce) {
    return <section className="report-state-card">Loading agents...</section>;
  }
  if (error && !hasLoadedOnce) {
    return <section className="report-state-card message-banner error">{error}</section>;
  }

  return (
    <section className="agents-workspace">
      {notice ? <div className="message-banner success">{notice}</div> : null}
      {error ? <div className="message-banner error">{error}</div> : null}
      <header className="agents-page-header">
        <div>
          <h1>My Agents</h1>
          <p>Overview of agents and their performance</p>
        </div>
        <div className="agents-top-actions">
          {loading ? <span className="agents-refreshing-indicator">Refreshing...</span> : null}
          <label className="search-box agents-search-box">
            <SearchIcon />
            <input
              type="text"
              placeholder="Search agents..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
        </div>
      </header>

      {!agents.length ? (
        <section className="report-state-card">No agents are currently visible in your scope.</section>
      ) : (
        <>
          <section className="agents-summary-grid">
            <SummaryCard
              label="Total Agents"
              value={summary.totalAgents}
              icon={<UserGroupIcon />}
              tone="blue"
            />
            <SummaryCard
              label="Total Leads"
              value={summary.totalLeads.toLocaleString("en-IN")}
              icon={<FilterIcon />}
              tone="green"
              breakdown={
                summary.b2cLeads != null
                  ? [
                      { label: 'B2C', value: summary.b2cLeads, bg: '#dbeafe', color: '#1d4ed8' },
                      { label: 'Franchise', value: summary.franchiseLeads, bg: '#ede9fe', color: '#6d28d9' },
                    ]
                  : undefined
              }
            />
            <SummaryCard
              label="Leads Assigned Today"
              value={summary.assignedToday.toLocaleString("en-IN")}
              icon={<PathIcon />}
              tone="violet"
            />
            <SummaryCard
              label="Conversion Rate"
              value={summary.conversionRate}
              icon={<PlusIcon />}
              tone="amber"
            />
          </section>

          {topPerformer ? (
            <section className="agents-top-performer-card">
              <div className="agents-top-performer-head">
                <span className="agents-top-performer-trophy" aria-hidden="true">🏆</span>
                <h3>Top Performer of the Week</h3>
              </div>
              <div className="agents-top-performer-body">
                <div className="agents-top-performer-identity">
                  <div className="agents-avatar agents-top-performer-avatar">
                    {initialsForName(topPerformer.name)}
                  </div>
                  <div>
                    <div className="agents-agent-name">{topPerformer.name}</div>
                    <div className="agents-agent-email">{topPerformer.email || "—"}</div>
                  </div>
                  <span className="agents-top-performer-badge">Top Performer</span>
                </div>
                <div className="agents-top-performer-stats">
                  <div>
                    <p>Total Leads</p>
                    <strong>{Number(topPerformer.active_leads) || 0}</strong>
                  </div>
                  <div>
                    <p>Converted</p>
                    <strong>{Number(topPerformer.converted_leads) || Math.round(((Number(topPerformer.conversion_rate) || 0) / 100) * (Number(topPerformer.active_leads) || 0))}</strong>
                  </div>
                  <div>
                    <p>Conversion Rate</p>
                    <strong>{(Number(topPerformer.conversion_rate) || 0).toFixed(1)}%</strong>
                  </div>
                </div>
              </div>
            </section>
          ) : null}

          {(attention.zeroConversion || attention.overdue || attention.inactive) ? (
            <section className="agents-attention-banner">
              <div className="agents-attention-head">
                <span className="agents-attention-icon" aria-hidden="true">⚠</span>
                <h3>Needs Attention</h3>
              </div>
              <div className="agents-attention-stats">
                <div>
                  <strong>{attention.zeroConversion} agent{attention.zeroConversion === 1 ? "" : "s"}</strong>
                  <span>with 0% conversion rate</span>
                </div>
                <div>
                  <strong>{attention.overdue} overdue lead{attention.overdue === 1 ? "" : "s"}</strong>
                  <span>pending follow-up</span>
                </div>
                <div>
                  <strong>{attention.inactive} idle agent{attention.inactive === 1 ? "" : "s"}</strong>
                  <span>
                    {isViewingToday
                      ? "no call dialed in the last 5 minutes"
                      : `no calls recorded on ${reportDateLabel}`}
                  </span>
                </div>
              </div>
            </section>
          ) : null}

          <section className="agents-filter-panel">
            <div className="agents-filter-grid">

              <label className="field-stack">
                <span>Call Activity</span>
                <select value={filters.activeStatus} onChange={(event) => updateFilter("activeStatus", event.target.value)}>
                  {["All", "Recently Active", "Idle"].map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </label>
              <label className="field-stack">
                <span>Geography</span>
                <select value={filters.geography} onChange={(event) => updateFilter("geography", event.target.value)}>
                  {geographyOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </label>
              <label className="field-stack">
                <span>Lead Load</span>
                <select value={filters.leadLoad} onChange={(event) => updateFilter("leadLoad", event.target.value)}>
                  {["All Loads", "High", "Medium", "Low"].map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </label>
              <label className="field-stack">
                <span>Performance</span>
                <select value={filters.performance} onChange={(event) => updateFilter("performance", event.target.value)}>
                  {["All Levels", "High", "Medium", "Low"].map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </label>
              <label className="field-stack">
                <span>Status</span>
                <select value={filters.status} onChange={(event) => updateFilter("status", event.target.value)}>
                  {["All Status", "online", "break", "offline", "on_call"].map((option) => (
                    <option key={option} value={option}>
                      {option === "All Status" ? option : option.replace("_", " ")}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field-stack">
                <span>Performance Date</span>
                <button
                  type="button"
                  className={`filter-toolbar-button compact-button date-filter-button agents-performance-date-button${!isViewingToday ? " has-range" : ""}`}
                  onClick={() => setDateModalOpen(true)}
                >
                  <CalendarIcon />
                  <span>{performanceDateLabel}</span>
                </button>
              </label>
              <button type="button" className="agents-reset-button" onClick={resetFilters}>
                Reset Filters
              </button>
            </div>
          </section>

          <section className="agents-table-panel">
            <div className="agents-toolbar-row">
              <div className="agents-toolbar-title">
                <span>Agent Activity</span>
                <InfoIconButton onClick={() => setColumnInfoOpen(true)} />
              </div>
              <div className="agents-toolbar-actions">
                <button type="button" className="secondary-button agents-secondary-action" onClick={onBulkReassign}>
                  <PathIcon />
                  <span>Bulk Reassign</span>
                </button>
                <button type="button" className="secondary-button agents-secondary-action" onClick={onUploadLeads}>
                  <CloudUploadIcon />
                  <span>Upload Leads</span>
                </button>
                <button type="button" className="filter-toolbar-button agents-primary-action" onClick={onAddAgent}>
                  <PlusIcon />
                  <span>Add Agent</span>
                </button>
              </div>
            </div>

            <div className="table-scroll">
              <table className="agents-table">
                <colgroup>
                  <col className="agents-col-agent" />
                  <col className="agents-col-status" />
                  <col className="agents-col-time" />
                  <col className="agents-col-metric" />
                  <col className="agents-col-metric" />
                  <col className="agents-col-metric" />
                  <col className="agents-col-metric" />
                  <col className="agents-col-metric" />
                  <col className="agents-col-time" />
                  <col className="agents-col-time" />
                  <col className="agents-col-break" />
                  <col className="agents-col-actions" />
                </colgroup>
                <thead>
                  <tr>
                    <th><AgentTableHeading icon={<UserGroupIcon />} iconTone="blue">Agent</AgentTableHeading></th>
                    <th className="agents-status-heading">
                      <AgentTableHeading icon={<PulseMiniIcon />} iconTone="green">Call Activity</AgentTableHeading>
                    </th>
                    <th><AgentTableHeading icon={<ClockIcon />} iconTone="violet">Active Time / Day</AgentTableHeading></th>
                    <th><AgentTableHeading icon={<UserGroupIcon />} iconTone="amber">Assigned Leads</AgentTableHeading></th>
                    <th><AgentTableHeading icon={<PhoneIcon />} iconTone="indigo">Calls Attempted</AgentTableHeading></th>
                    <th><AgentTableHeading icon={<PhoneIcon />} iconTone="indigo">Unique Attempts</AgentTableHeading></th>
                    <th><AgentTableHeading icon={<PhoneIcon />} iconTone="teal">Connected Calls</AgentTableHeading></th>
                    <th><AgentTableHeading icon={<PhoneIcon />} iconTone="teal">Unique Connected</AgentTableHeading></th>
                    <th><AgentTableHeading icon={<ClockIcon />} iconTone="rose">Talk Time</AgentTableHeading></th>
                    <th><AgentTableHeading icon={<ClockIcon />} iconTone="rose">Dialed Time</AgentTableHeading></th>
                    <th><AgentTableHeading icon={<ClockIcon />} iconTone="gold">Break</AgentTableHeading></th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAgents.map((agent) => (
                    <tr key={agent.id} style={agent.is_active === false ? { opacity: 0.55 } : undefined}>
                      <td>
                        <div className="agents-agent-cell">
                          <div className={`agents-avatar agents-avatar-presence ${availabilityTone(agent)} ${agent.daily_active_status === "active" ? "active" : ""}`}>
                            {initialsForName(agent.name)}
                          </div>
                          <div>
                            <div className="agents-agent-name">
                              {agent.name}
                              {agent.is_active === false && (
                                <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 999, background: "#fee2e2", color: "#b91c1c" }}>
                                  Inactive
                                </span>
                              )}
                            </div>
                            <div className="agents-agent-email">{agent.email || "—"}</div>
                            <div className="agents-agent-meta">
                              {isAdmin && (agent.reports_to_name || agent.manager_name) ? (
                                <span>Reports to {agent.reports_to_name || agent.manager_name}</span>
                              ) : null}
                              {!isManager && !isAdmin && agent.manager_name ? (
                                <span>Manager {agent.manager_name}</span>
                              ) : null}
                              {regionNamesForAgent(agent).length ? (
                                <span>{regionNamesForAgent(agent).join(", ")}</span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="agents-status-cell">
                        <div className="agents-status-stack">
                          {(() => {
                            const lastDialedSince = agent.last_call_started_at
                              ? formatTimeSince(agent.last_call_started_at)
                              : "—";
                            const hasValidLastDialed = lastDialedSince !== "—";
                            return (
                              <>
                          <span
                            className={`agent-badge agents-status-badge ${agent.daily_active_status === "active" ? "green" : "gold"}`}
                            title={hasValidLastDialed ? `Last dialed ${lastDialedSince} ago` : `No calls dialed on ${reportDateLabel}`}
                          >
                            <span
                              className={`agents-status-dot ${agent.daily_active_status === "active" ? "green" : "gold"}`}
                              aria-hidden="true"
                            />
                            {agent.daily_active_status === "active" ? "Recently Active" : "Idle"}
                          </span>
                          <span className="agents-status-subtext">
                            {hasValidLastDialed
                              ? `Last dialed ${lastDialedSince} ago`
                              : `No calls on ${reportDateLabel}`}
                          </span>
                          <span className={`agents-availability-pill ${availabilityTone(agent)}`}>
                            {availabilityLabel(agent)}
                          </span>
                              </>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="mono-text agents-metric-cell">{formatDuration(agent.active_total_seconds_today)}</td>
                      <td className="mono-text agents-metric-cell amber">{agent.total_assigned_leads ?? 0}</td>
                      <td className="mono-text agents-metric-cell indigo">{agent.total_attempts_today ?? 0}</td>
                      <td className="mono-text agents-metric-cell indigo">{agent.total_unique_attempts_today ?? 0}</td>
                      <td className="mono-text agents-metric-cell teal">{agent.total_connected_today ?? 0}</td>
                      <td className="mono-text agents-metric-cell teal">{agent.total_unique_connected_today ?? 0}</td>
                      <td className="mono-text agents-metric-cell rose">{formatDuration(agent.total_connected_seconds_today)}</td>
                      <td className="mono-text agents-metric-cell rose">
                        {formatDuration(agent.total_dialed_seconds_today ?? agent.active_total_seconds_today)}
                      </td>
                      <td className="agents-break-cell">
                        <select
                          className="agents-break-select"
                          value={agent.break_reason || ""}
                          disabled={breakSaving[agent.id]}
                          onChange={(event) => handleBreakChange(agent, event.target.value)}
                          title={agent.break_reason ? `On ${breakLabel(agent.break_reason)}` : "Set break type"}
                        >
                          <option value="">Break type</option>
                          {BREAK_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <div className="agents-actions-cell">
                          <button
                            type="button"
                            className="agents-action-icon"
                            aria-label="View agent"
                            onClick={() => setSelectedAgent(agent)}
                          >
                            <EyeIcon />
                          </button>
                          <button
                            type="button"
                            className="agents-action-icon success"
                            aria-label="Assign lead"
                            onClick={() => onAssignLead(agent)}
                          >
                            <PlusIcon />
                          </button>
                          <button
                            type="button"
                            className="agents-action-text"
                            aria-label="Edit agent"
                            onClick={() => openEditAgent(agent)}
                          >
                            <EditIcon />
                            <span>Edit Agent</span>
                          </button>
                          {agent.is_active === false ? (
                            <button
                              type="button"
                              className="agents-action-text success"
                              aria-label="Activate agent"
                              onClick={() => handleActivateAgent(agent)}
                            >
                              <PlusIcon />
                              <span>Activate</span>
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="agents-action-text danger"
                              aria-label="Deactivate agent"
                              onClick={() => handleDeactivateAgent(agent)}
                            >
                              <CloseIcon />
                              <span>Deactivate</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!filteredAgents.length ? (
                    <tr>
                      <td colSpan={12} className="table-state">
                        No agents matched the current filters.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
      <AgentDetailModal
        agent={selectedAgent}
        onAddLead={(agent) => {
          setSelectedAgent(null);
          onAssignLead(agent);
        }}
        onClose={() => setSelectedAgent(null)}
        onManageAgent={() => {
          if (selectedAgent) {
            openEditAgent(selectedAgent);
          }
        }}
      />
      <EditAgentModal
        agent={editingAgent}
        draft={editDraft}
        managers={managers}
        onChange={(field, value) => setEditDraft((current) => ({ ...current, [field]: value }))}
        onClose={() => {
          setEditingAgent(null);
          setEditDraft({});
        }}
        onSubmit={handleEditAgentSubmit}
        saving={savingAgent}
      />
      <PhoneRevealModal
        agent={phoneRevealAgent}
        error={phoneRevealError}
        loading={phoneRevealLoading}
        onClose={() => setPhoneRevealAgent(null)}
        reveals={phoneRevealLogs}
      />
      <DateRangeModal
        open={dateModalOpen}
        startValue={reportDateFrom}
        endValue={reportDateTo}
        onClose={() => setDateModalOpen(false)}
        onApply={(start, end) => {
          setReportDateFrom(start || "");
          setReportDateTo(end || start || "");
          setDateModalOpen(false);
        }}
      />
      {columnInfoOpen ? <AgentColumnInfoModal onClose={() => setColumnInfoOpen(false)} /> : null}
    </section>
  );
}

export default MyAgentsView;
